import { NavLink as RouterLink, Outlet, useLocation } from 'react-router-dom';
import {
    ActionIcon,
    AppShell,
    Avatar,
    Badge,
    Divider,
    Group,
    Image,
    Menu,
    NavLink,
    ScrollArea,
    Text,
    UnstyledButton,
    useComputedColorScheme,
    useMantineColorScheme,
} from '@mantine/core';
import { ExternalLink, LogOut, Moon, Sun } from 'lucide-react';
import logo from '@/assets/logo.png';
import { isNavActive, NAV_LEGACY, NAV_MAIN } from '@/navigation';
import { clearToken } from '@/auth/session';
import { useMe } from '@/auth/useMe';
import { TabBar } from './TabBar';

function Brand() {
    return (
        <Group gap={10} wrap="nowrap">
            <Image src={logo} w={32} h={32} radius="sm" alt="" />
            <Text fw={900} fz="lg" lts={-0.5} style={{ whiteSpace: 'nowrap' }}>
                FAST
                <Text span c="brand" inherit>
                    &
                </Text>
                FRESH
            </Text>
            <Badge size="xs" variant="outline" color="gray" visibleFrom="xs">
                admin
            </Badge>
        </Group>
    );
}

function ColorSchemeToggle() {
    const { setColorScheme } = useMantineColorScheme();
    const scheme = useComputedColorScheme('dark');
    return (
        <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label="Сменить тему"
            onClick={() => setColorScheme(scheme === 'dark' ? 'light' : 'dark')}
        >
            {scheme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </ActionIcon>
    );
}

function UserMenu() {
    const { data: me } = useMe();
    const name = me?.name || me?.username || 'Админ';
    return (
        <Menu position="bottom-end" width={240} shadow="md">
            <Menu.Target>
                <UnstyledButton aria-label="Профиль">
                    <Avatar radius="xl" size={34} color="brand" variant="filled">
                        {name.slice(0, 1).toUpperCase()}
                    </Avatar>
                </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>
                    <Text size="sm" fw={600} c="bright">
                        {name}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {me?.role}
                        {me?.username ? ` · @${me.username}` : ''}
                    </Text>
                </Menu.Label>
                <Menu.Item color="red" leftSection={<LogOut size={16} />} onClick={clearToken}>
                    Выйти
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
}

export function AppLayout() {
    const location = useLocation();

    // На телефоне боковое меню не показываем вовсе — навигация в нижнем таббаре.
    return (
        <AppShell
            header={{ height: 60 }}
            navbar={{ width: 248, breakpoint: 'sm', collapsed: { mobile: true } }}
            padding={{ base: 'sm', sm: 'lg' }}
        >
            <AppShell.Header>
                <Group h="100%" px="md" justify="space-between" wrap="nowrap">
                    <UnstyledButton component={RouterLink} to="/">
                        <Brand />
                    </UnstyledButton>
                    <Group gap="xs" wrap="nowrap">
                        <ColorSchemeToggle />
                        <UserMenu />
                    </Group>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar p="sm">
                <AppShell.Section grow component={ScrollArea}>
                    {NAV_MAIN.map((item) => (
                        <NavLink
                            key={item.label}
                            component={RouterLink}
                            to={item.to!}
                            end={item.to === '/'}
                            label={item.label}
                            leftSection={<item.icon size={18} />}
                            variant="light"
                            style={{ borderRadius: 'var(--mantine-radius-md)' }}
                            active={isNavActive(location.pathname, item.to!)}
                        />
                    ))}
                    <Divider my="sm" label="В старой админке" labelPosition="left" />
                    {NAV_LEGACY.map((item) => (
                        <NavLink
                            key={item.label}
                            href={item.href}
                            target="_blank"
                            rel="noopener"
                            label={item.label}
                            c="dimmed"
                            leftSection={<item.icon size={18} />}
                            rightSection={<ExternalLink size={14} />}
                            style={{ borderRadius: 'var(--mantine-radius-md)' }}
                        />
                    ))}
                </AppShell.Section>
            </AppShell.Navbar>

            <AppShell.Main className="ff-main">
                <Outlet />
            </AppShell.Main>
            <TabBar />
        </AppShell>
    );
}
